import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { firmasPublicAPI } from '../api';

const Cargando = ({ texto }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500 text-sm">{texto}</div>
);

const LienzoFirma = ({ onConfirmar, onCancelar, enviando }) => {
  const canvasRef = useRef(null);
  const [vacio, setVacio] = useState(true);
  const dibujando = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const ancho = canvas.parentElement.clientWidth - 4;
    const alto = Math.min(260, Math.max(160, Math.round(ancho * 0.38)));
    canvas.width = ancho * ratio;
    canvas.height = alto * ratio;
    canvas.style.width = `${ancho}px`;
    canvas.style.height = `${alto}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const posicion = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const punto = e.touches ? e.touches[0] : e;
    return { x: punto.clientX - rect.left, y: punto.clientY - rect.top };
  };

  const empezar = (e) => {
    e.preventDefault();
    dibujando.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = posicion(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const mover = (e) => {
    if (!dibujando.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = posicion(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setVacio(false);
  };

  const terminar = () => { dibujando.current = false; };

  const limpiar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setVacio(true);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-3"
         data-testid="firma-lienzo-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl p-4 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Firme en el recuadro</h2>
          <p className="text-xs text-slate-500">Use el dedo, el lápiz táctil o el ratón.</p>
        </div>
        <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 p-0.5">
          <canvas
            ref={canvasRef}
            className="touch-none block rounded"
            onMouseDown={empezar}
            onMouseMove={mover}
            onMouseUp={terminar}
            onMouseLeave={terminar}
            onTouchStart={empezar}
            onTouchMove={mover}
            onTouchEnd={terminar}
            data-testid="firma-canvas"
          />
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button className="px-3 py-2 text-sm rounded border border-slate-300" onClick={onCancelar}
                  data-testid="firma-cancelar-btn">Cancelar</button>
          <button className="px-3 py-2 text-sm rounded border border-slate-300" onClick={limpiar}
                  data-testid="firma-limpiar-btn">Borrar</button>
          <button
            className="px-4 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
            disabled={vacio || enviando}
            onClick={() => onConfirmar(canvasRef.current.toDataURL('image/png'))}
            data-testid="firma-confirmar-btn"
          >
            {enviando ? 'Firmando…' : 'Confirmar firma'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Estado de una firma conjunta: quien ha firmado ya y quien falta.
const PanelGrupo = ({ info }) => (
  <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 space-y-1.5"
       data-testid="firma-panel-grupo">
    <div className="text-xs font-semibold text-indigo-900">
      Firma conjunta · {info.firmantes_firmados} de {info.firmantes_total} firmas hechas
    </div>
    <p className="text-xs text-indigo-800">
      Este documento lo firman {info.firmantes_total} personas, en cualquier orden. El documento
      final llevará todas las firmas.
    </p>
    <ul className="space-y-0.5">
      {(info.firmantes || []).map((f, i) => (
        <li key={i} className="flex items-center justify-between gap-2 text-xs text-slate-700"
            data-testid={`firma-grupo-firmante-${i}`}>
          <span className="truncate">{f.nombre}{f.soy_yo ? ' (usted)' : ''}</span>
          <span className={f.estado === 'firmado' ? 'text-green-700' : 'text-amber-700'}>
            {f.estado === 'firmado'
              ? `firmado${f.firmado_at ? ` · ${new Date(f.firmado_at).toLocaleDateString('es-ES')}` : ''}`
              : 'pendiente'}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const FirmarDocumento = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [cargandoInfo, setCargandoInfo] = useState(true);
  const [nif, setNif] = useState('');
  const [validando, setValidando] = useState(false);
  const [error, setError] = useState('');
  const [documento, setDocumento] = useState(null);
  const [colocacion, setColocacion] = useState(null);
  const [mostrarLienzo, setMostrarLienzo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [firmado, setFirmado] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await firmasPublicAPI.info(token);
        setInfo(res.data);
        if (res.data?.estado === 'firmado') setFirmado(true);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Enlace no válido');
      } finally {
        setCargandoInfo(false);
      }
    })();
  }, [token]);

  const acceder = async (e) => {
    e?.preventDefault();
    setValidando(true);
    setError('');
    try {
      const res = await firmasPublicAPI.acceder(token, nif);
      setDocumento(res.data);
      const ancla = res.data?.ancla || {};
      if (ancla.rect_rel && ancla.rect_rel.length === 4) {
        setColocacion({ pagina: ancla.pagina, rect_rel: ancla.rect_rel });
      } else {
        setColocacion(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se ha podido validar su identidad');
    } finally {
      setValidando(false);
    }
  };

  const multi = !!info?.multifirma;
  // v135: puntos de firma marcados por el despacho (el principal es el primero)
  const puntos = (documento?.ancla?.casillas || []).filter((p) => p?.rect_rel?.length === 4);
  const variosPuntos = puntos.length > 1;
  const fijo = multi || variosPuntos;

  const colocarEnClic = (e, indice) => {
    if (fijo) return;   // la casilla la ha colocado el despacho
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;
    const ancho = 0.34;
    const alto = 0.075;
    const x0 = Math.min(Math.max(relX - ancho / 2, 0.03), 0.97 - ancho);
    const y0 = Math.min(Math.max(relY - alto / 2, 0.02), 0.98 - alto);
    setColocacion({ pagina: indice, rect_rel: [x0, y0, x0 + ancho, y0 + alto] });
  };

  const enviarFirma = async (dataUrl) => {
    if (!colocacion) return;
    setEnviando(true);
    try {
      const res = await firmasPublicAPI.firmar(token, {
        acceso: documento.acceso,
        firma_base64: dataUrl,
        pagina: fijo ? -1 : colocacion.pagina,
        rect_rel: fijo ? [] : colocacion.rect_rel,
      });
      setResultado(res.data);
      setMostrarLienzo(false);
      setFirmado(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo completar la firma');
      setMostrarLienzo(false);
    } finally {
      setEnviando(false);
    }
  };

  if (cargandoInfo) return <Cargando texto="Cargando documento…" />;

  if (firmado) {
    const parcial = !!(resultado?.multifirma && resultado?.pendientes > 0);
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm max-w-md w-full p-6 text-center space-y-3"
             data-testid="firma-completada">
          <div className="text-3xl">✓</div>
          <h1 className="text-lg font-semibold text-slate-900">
            {parcial ? 'Su firma ha quedado registrada' : 'Documento firmado'}
          </h1>
          <p className="text-sm text-slate-600">
            {parcial
              ? `Gracias. El documento queda pendiente de la firma de ${resultado.pendientes} persona(s) más. Cuando hayan firmado todas, recibirá el documento definitivo con todas las firmas.`
              : 'Gracias. Su firma se ha registrado correctamente y el despacho ya tiene el documento. Recibirá una copia por email.'}
          </p>
          {parcial && (
            <div className="text-xs text-slate-500" data-testid="firma-progreso-grupo">
              Firmas completadas: {resultado.firmantes_firmados} de {resultado.firmantes_total}
            </div>
          )}
          {!parcial && documento?.acceso && (
            <a
              className="inline-block text-sm text-indigo-600 hover:underline"
              href={firmasPublicAPI.urlDescarga(token, documento.acceso)}
              data-testid="firma-descargar-link"
            >
              Descargar el documento firmado
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!documento) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <form onSubmit={acceder} className="bg-white rounded-xl shadow-sm max-w-md w-full p-6 space-y-4"
              data-testid="firma-acceso-form">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Firma de documento</h1>
            <p className="text-sm text-slate-600 mt-1">
              {info?.nombre_documento
                ? <>Documento: <strong>{info.nombre_documento}</strong></>
                : 'Documento pendiente de firma'}
            </p>
            {info?.firmante_nombre && (
              <p className="text-sm text-slate-600 mt-1" data-testid="firma-firmante-nombre">
                Firmante: <strong>{info.firmante_nombre}</strong>
              </p>
            )}
          </div>
          {multi && <PanelGrupo info={info} />}
          {info?.requiere_nif === false ? (
            <p className="text-sm text-slate-600" data-testid="firma-sin-nif-aviso">
              Pulse el botón para ver el documento y firmarlo.
            </p>
          ) : (
            <div>
              <label className="text-sm text-slate-700">Introduzca su NIF/DNI para continuar</label>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm uppercase"
                placeholder="12345678A"
                value={nif}
                onChange={(ev) => setNif(ev.target.value)}
                data-testid="firma-nif-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Solo se usa para verificar que es usted el destinatario del documento.
              </p>
            </div>
          )}
          {error && <div className="text-sm text-red-600" data-testid="firma-acceso-error">{error}</div>}
          <button
            type="submit"
            className="w-full bg-indigo-600 text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            disabled={validando || (info?.requiere_nif !== false && !nif.trim())}
            data-testid="firma-acceder-btn"
          >
            {validando ? 'Comprobando…' : 'Ver el documento'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-28">
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="text-sm font-semibold text-slate-900 truncate">{documento.nombre_documento}</div>
          <div className="text-xs text-slate-500">
            {variosPuntos
              ? `Este documento se firma en ${puntos.length} puntos. Firme una sola vez: su firma se estampará en todos.`
              : multi
                ? `Su casilla de firma ya está reservada en la página ${(colocacion?.pagina ?? 0) + 1}: pulse «Firmar aquí».`
                : colocacion
                  ? `La firma se colocará en la página ${colocacion.pagina + 1}. Puede tocar el documento para moverla.`
                  : 'Toque el documento en el lugar donde quiere firmar.'}
          </div>
          {multi && (
            <div className="text-xs text-indigo-700 mt-1" data-testid="firma-multifirma-aviso">
              Firma conjunta: {info.firmantes_firmados} de {info.firmantes_total} firmas hechas.
              El documento final llevará las firmas de todos.
            </div>
          )}
          {documento.ancla?.integra && (
            <div className="text-xs text-amber-700 mt-1" data-testid="firma-integra-aviso">
              Su firma se estampará también en el margen de todas las páginas (firma documental).
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-3">
        {documento.paginas?.map((p) => (
          <div key={p.indice} className={`relative bg-white shadow-sm rounded overflow-hidden ${fijo ? '' : 'cursor-crosshair'}`}
               onClick={(e) => colocarEnClic(e, p.indice)}
               data-testid={`firma-pagina-${p.indice}`}>
            <img src={p.imagen} alt={`Página ${p.indice + 1}`} className="w-full block" />
            {(variosPuntos
              ? puntos.filter((pt) => pt.pagina === p.indice)
              : (colocacion?.pagina === p.indice ? [colocacion] : [])
            ).map((pt, i) => (
              <div
                key={i}
                className="absolute border-2 border-indigo-500 bg-indigo-500/10 rounded pointer-events-none flex items-center justify-center"
                style={{
                  left: `${pt.rect_rel[0] * 100}%`,
                  top: `${pt.rect_rel[1] * 100}%`,
                  width: `${(pt.rect_rel[2] - pt.rect_rel[0]) * 100}%`,
                  height: `${(pt.rect_rel[3] - pt.rect_rel[1]) * 100}%`,
                }}
                data-testid={variosPuntos ? `firma-marcador-${p.indice}-${i}` : 'firma-marcador'}
              >
                <span className="text-[10px] text-indigo-700 font-medium">
                  {variosPuntos
                    ? 'Su firma aquí'
                    : (multi ? `Casilla de ${info.firmante_nombre || 'su firma'}` : 'Su firma aquí')}
                </span>
              </div>
            ))}
            <div className="absolute bottom-1 right-2 text-[10px] text-slate-400">
              {p.indice + 1} / {documento.paginas.length}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="fixed bottom-24 left-0 right-0 px-4">
          <div className="max-w-3xl mx-auto bg-red-50 text-red-700 text-sm rounded p-3" data-testid="firma-error-banner">
            {error}
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="text-xs text-slate-500 flex-1">
            Al firmar acepta que su firma manuscrita se incorpore al documento junto a la
            fecha, su IP y el dispositivo utilizado como evidencia.
          </div>
          <button
            className="bg-indigo-600 text-white rounded px-5 py-2.5 text-sm disabled:opacity-50"
            disabled={!colocacion}
            onClick={() => setMostrarLienzo(true)}
            data-testid="firma-abrir-lienzo-btn"
          >
            {colocacion
              ? (variosPuntos ? `Firmar (${puntos.length} puntos)` : 'Firmar aquí')
              : 'Elija el lugar'}
          </button>
        </div>
      </div>

      {mostrarLienzo && (
        <LienzoFirma
          enviando={enviando}
          onCancelar={() => setMostrarLienzo(false)}
          onConfirmar={enviarFirma}
        />
      )}
    </div>
  );
};

export default FirmarDocumento;
